/* utility for rendering google product schema JSON blobs to improve SEO availability
 * https://developers.google.com/search/docs/data-types/product
 * */
import React from "react";

import {
    IImageURL,
    IProductSKU,
    IProductUPC,
    ISafeHTML,
    IWebPageURL,
} from "../models/nominals";

interface IProps {
    name: string;
    imageUrl?: IImageURL;
    description: ISafeHTML;
    sku: IProductUPC | IProductSKU;
    brandName: string;
    ratingValue: number | null;
    reviewCount: number;
    // Enhanced properties for better SEO
    url?: IWebPageURL | string;
    price?: number | string;
    priceCurrency?: string;
    availability?: "InStock" | "OutOfStock" | "PreOrder";
    category?: string;
}

interface IState {}

// TODO(ssr, LEVEL 3 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 3/10 due to JSON-LD schema generation with conditional fields, but no state or lifecycle methods.
interface ProductSchema {
    "@context": string;
    "@type": string;
    "name": string;
    "description": ISafeHTML;
    "sku": IProductUPC | IProductSKU;
    "brand": {
        "@type": string;
        "name": string;
    };
    "image"?: IImageURL[];
    "url"?: IWebPageURL | string;
    "aggregateRating"?: {
        "@type": string;
        "ratingValue": number;
        "reviewCount": number;
    };
    "offers"?: {
        "@type": string;
        "price": number | string;
        "priceCurrency": string;
        "availability": string;
        "url"?: IWebPageURL | string;
    };
    "category"?: string;
}

export class ProductSchemaTag extends React.Component<IProps, IState> {
    render() {
        const schema: ProductSchema = {
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": this.props.name,
            "description": this.props.description,
            "sku": this.props.sku,
            "brand": {
                "@type": "Brand",
                "name": this.props.brandName,
            },
        };

        if (this.props.imageUrl) {
            schema.image = [this.props.imageUrl];
        }

        if (this.props.url) {
            schema.url = this.props.url;
        }

        if (this.props.ratingValue && this.props.reviewCount) {
            schema.aggregateRating = {
                "@type": "AggregateRating",
                "ratingValue": this.props.ratingValue,
                "reviewCount": this.props.reviewCount,
            };
        }

        if (this.props.price) {
            schema.offers = {
                "@type": "Offer",
                "price": this.props.price,
                "priceCurrency": this.props.priceCurrency || "USD",
                "availability": `https://schema.org/${this.props.availability || "InStock"}`,
            };

            if (this.props.url) {
                schema.offers.url = this.props.url;
            }
        }
        if (this.props.category) {
            schema.category = this.props.category;
        }

        return (
            <script type="application/ld+json">{JSON.stringify(schema)}</script>
        );
    }
}
export default ProductSchemaTag;
